/**
 * CameraHttpSettingsSheet — operator-facing HTTP config push UI.
 *
 * Renders only for cameras with ingestion_type='http'. PATCHes
 * /api/cameras/{id}/ with `desired_pir_interval`, `desired_photo_size`,
 * `desired_video_resolution`, `desired_video_length`, and
 * `desired_extra_settings`. Backend translates to firmware enum codes and
 * exposes them at GET /InfraredCamera/SetCode/imei/{IMEI}/; the camera
 * firmware polls that endpoint on its own schedule.
 *
 * Settings status (#051, #396): a changed setting is "pending" (amber) until
 * the camera fetches it, "sent" (blue) once the server has handed it over
 * (then it is not served again), and "applied" (green) when the camera's next
 * uploaded Menu.txt shows the value. The per-field markers below follow the
 * server's settings_pending_fields, each entry carrying its own `sent` flag.
 */

import * as Dialog from '@radix-ui/react-dialog'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { X, Loader2, SlidersHorizontal, AlertTriangle } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { camerasApi } from '@/api/endpoints'
import { useAuthStore } from '@/store/authStore'
import { canChangeCameraSettings, hasFullRights } from '@/lib/sharePermissions'
import { useTranslation } from 'react-i18next'
import type { Camera, CameraUpdateRequest } from '@/types'
import {
  UNCHANGED, emptyFormState, formStateFromCamera, extrasFromFormState,
  type HttpSettingsFormState,
} from '@/lib/httpSettingsForm'
import { HttpSettingsFields } from './HttpSettingsFields'

interface CameraHttpSettingsSheetProps {
  camera: Camera
  open: boolean
  onOpenChange: (open: boolean) => void
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

function fieldChanged<T>(initial: T, current: T): boolean {
  return JSON.stringify(initial) !== JSON.stringify(current)
}

function PendingBadge({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span className="inline-flex items-center rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-xs font-medium">
      {count} pending push
    </span>
  )
}

// ─────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────

export function CameraHttpSettingsSheet({ camera, open, onOpenChange }: CameraHttpSettingsSheetProps) {
  const queryClient = useQueryClient()
  const { t } = useTranslation('cameras')
  // #410 — what this person may change; the server stays the gate. A settings tick edits the shooting fields, never the plumbing rows.
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser)
  const canEdit = canChangeCameraSettings(camera, isSuperuser)
  const lockPlumbing = !hasFullRights(camera, isSuperuser)

  const [form, setForm] = useState<HttpSettingsFormState>(emptyFormState)
  const set = useCallback(<K extends keyof HttpSettingsFormState>(k: K, v: HttpSettingsFormState[K]) =>
    setForm((f) => ({ ...f, [k]: v })), [])

  // Snapshot of "what was loaded" for diff-on-save logic
  const initialState = useMemo(() => {
    const ex = camera.desired_extra_settings || {}
    return {
      pirInterval: camera.desired_pir_interval ?? UNCHANGED,
      photoSize: camera.desired_photo_size || UNCHANGED,
      videoResolution: camera.desired_video_resolution || UNCHANGED,
      videoLength: camera.desired_video_length ?? UNCHANGED,
      extras: ex,
    }
  }, [camera])

  // Reset on open / camera change.
  useEffect(() => { if (open) setForm(formStateFromCamera(camera)) }, [open, camera])

  // ── Build the PATCH payload from current form state vs initial.
  const buildPayload = (): CameraUpdateRequest => {
    const payload: CameraUpdateRequest = {}

    // Bucket-1 typed columns
    if (form.pirInterval !== initialState.pirInterval) {
      payload.desired_pir_interval = form.pirInterval === UNCHANGED ? null : form.pirInterval
    }
    if (form.photoSize !== initialState.photoSize) {
      payload.desired_photo_size = form.photoSize === UNCHANGED ? '' : form.photoSize
    }
    if (form.videoResolution !== initialState.videoResolution) {
      payload.desired_video_resolution = form.videoResolution === UNCHANGED ? '' : form.videoResolution
    }
    if (form.videoLength !== initialState.videoLength) {
      payload.desired_video_length = form.videoLength === UNCHANGED ? null : form.videoLength
    }

    const extras = extrasFromFormState(form)
    if (fieldChanged(initialState.extras, extras)) payload.desired_extra_settings = extras
    return payload
  }

  // ── PATCH mutation
  const updateMutation = useMutation({
    mutationFn: (payload: CameraUpdateRequest) => camerasApi.update(camera.id, payload),
    onSuccess: () => {
      toast.success('Settings queued for next camera check-in')
      queryClient.invalidateQueries({ queryKey: ['cameras'] })
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data
      const msg = data
        ? Object.entries(data).map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join('; ')
        : 'Save failed'
      toast.error(msg)
    },
  })

  const handleSave = () => {
    const payload = buildPayload()
    if (Object.keys(payload).length === 0) {
      toast.message('No changes to save')
      return
    }
    updateMutation.mutate(payload)
  }

  const isSaving = updateMutation.isPending

  // #052 — the count now comes from the backend (camera.settings_pending_count,
  // same pending_menu_delta source as settings_state) so it can't contradict
  // the applied/pending badge. The old local memo counted "settings
  // configured", which falsely showed e.g. "7 pending push" for an applied
  // camera.

  // #106 — which controls the backend says the camera has not confirmed yet.
  const pendingFields = useMemo(
    () => new Set((camera.settings_pending_fields ?? []).map((f) => f.field)),
    [camera.settings_pending_fields],
  )
  // #396 — the subset already handed to the camera (shown blue, "awaiting
  // confirmation") instead of amber ("not applied yet").
  const sentFields = useMemo(
    () => new Set((camera.settings_pending_fields ?? []).filter((f) => f.sent).map((f) => f.field)),
    [camera.settings_pending_fields],
  )

  return (
    <>
    <Dialog.Root open={open} onOpenChange={isSaving ? undefined : onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-full max-w-2xl border-l border-border bg-background shadow-elevation-3 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right duration-300">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
            <div className="flex items-center gap-2.5 min-w-0">
              <SlidersHorizontal className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <Dialog.Title className="text-base font-semibold truncate">HTTP Camera Settings</Dialog.Title>
                <p className="text-xs text-muted-foreground truncate">{camera.name} · {camera.imei}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {camera.settings_state === 'applied' && (
                <span className="inline-flex items-center rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">Settings applied</span>
              )}
              {camera.settings_state === 'pending' && (
                <span className="inline-flex items-center rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 border border-amber-500/20">Settings pending</span>
              )}
              {camera.settings_state === 'sent' && (
                <span className="inline-flex items-center rounded-full bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium text-sky-600 dark:text-sky-400 border border-sky-500/20">Settings sent</span>
              )}
              {/* #396 — counts only what has not been handed over yet, so a fully sent camera shows the blue chip alone. */}
              <PendingBadge count={camera.settings_pending_fields?.filter((f) => !f.sent).length ?? camera.settings_pending_count} />
              <Dialog.Close asChild>
                <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8" disabled={isSaving}>
                  <X className="h-4 w-4" />
                </Button>
              </Dialog.Close>
            </div>
          </div>

          {/* Status banner — explains the three states (#396): pending -> sent -> applied. */}
          <div className="px-5 py-3 bg-muted/40 border-b border-border shrink-0 text-xs text-muted-foreground flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            <p>
              A changed setting is handed to the camera on its next settings request and shown as Sent. It turns to Applied when the camera's next menu report confirms it. Many cameras send that report only once a day, so confirmation can take a while.
            </p>
          </div>

          {!canEdit && (
            <p className="px-5 py-3 border-b border-border shrink-0 text-xs text-amber-700 dark:text-amber-400">{t('edit.viewOnlyNote')}</p>
          )}

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
            <fieldset disabled={!canEdit} className="contents">
            <HttpSettingsFields
              form={form}
              set={set}
              lockPlumbing={lockPlumbing}
              disabled={!canEdit}
              pendingFields={pendingFields}
              sentFields={sentFields}
              reported={{
                pirInterval: camera.pir_interval !== null ? `${camera.pir_interval}s` : undefined,
                photoSize: camera.photo_size ? `${camera.photo_size} MP` : undefined,
                videoResolution: camera.video_resolution || undefined,
                videoLength: camera.video_length !== null ? `${camera.video_length}s` : undefined,
              }}
            />
            </fieldset>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border shrink-0">
            <Dialog.Close asChild>
              <Button variant="outline" disabled={isSaving}>Cancel</Button>
            </Dialog.Close>
            {canEdit && (
              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                Save changes
              </Button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
    </>
  )
}
